import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useCallback, useMemo } from "react";
import { useIntl } from "react-intl";
import { BOARD_EDIT_CONFIG } from "../../../../constants/modal.constant";
import { useBoardCore } from "../../../../cores/boards";
import { Board } from "../../../../cores/boards/interfaces/api/get-board";
import { useGlobalCore } from "../../../../cores/globals";
import { useMeCore } from "../../../../cores/me";
import { getFirstLetters } from "../../../utils/stringUtils";
import Avatar from "../../Avatar";
import { TooltipWembii } from "../../TooltipWembii";
import EditBoard from "../modal/EditBoard";

interface Props {
  board: Board;
}

export default function UserBoardContainer({ board }: Props) {
  const intl = useIntl();
  const { turnOnModal } = useGlobalCore();
  const { setBoardUpdate } = useBoardCore();
  const {
    user,
    isAdmin,
    isReseller,
    companyRole
  } = useMeCore();

  const openAssignmentUsers = useCallback(() => {
    setBoardUpdate({
      name: board.name,
      color: board.color,
      currency: board.currency,
      roles: board.userRoles,
      isChanged: false,
    });

    turnOnModal(
      BOARD_EDIT_CONFIG,
      <EditBoard board={board} />
    );
  }, [turnOnModal, setBoardUpdate, board]);

  const canModifyBoard = useMemo(() => 
    isAdmin ||
    isReseller ||
    companyRole?.owner ||
    companyRole?.admin ||
    board.userRoles.find((role) => role.user.id === user?.id)?.owner
  , [isAdmin, isReseller, companyRole, board, user]);

  const maxVisibleMembers = 3;
  const visibleMembers = useMemo(
    () => board.userRoles.slice(0, maxVisibleMembers),
    [board.userRoles]
  );
  const hiddenMembersCount = useMemo(
    () => Math.max(board.userRoles.length - maxVisibleMembers, 0),
    [board.userRoles.length]
  );
  
  return (
    <div
      className="user-cascade-container crm-members-container d-flex align-items-center justify-content-start flex-wrap"
      style={{ minHeight: 40 }}
    >
      <div className="board-members-stack d-flex align-items-center">
      {visibleMembers.map((userRole, index) => (
        <div key={userRole.id} className="user-avatar board-member-item" style={{ zIndex: maxVisibleMembers - index }}>
          <Avatar
            key={index}
            id={`user-${board.id}-${userRole.user.id}`}
            className="text-dark-50 pointer"
            image={userRole.user.avatarUrl}
            label={getFirstLetters(`${userRole.user.lastName} ${userRole.user.firstName}`)}
            icon={
              <FontAwesomeIcon
                icon={["fas", "user"]}
                size="1x"
              />
            }
          />
          <TooltipWembii 
            id={`user-${board.id}-${userRole.user.id}`}
            text={`${userRole.user.lastName} ${userRole.user.firstName}`} 
          />
        </div>
      ))}
      {hiddenMembersCount > 0 && (
        <div className="user-avatar board-member-item board-members-overflow">
          <Avatar
            id={`board-${board.id}-overflow`}
            className="text-dark-50"
            label={`+${hiddenMembersCount}`}
            backgroundColor="#eef3ff"
            color="#3556a8"
            labelFontSize={12}
          />
          <TooltipWembii
            id={`board-${board.id}-overflow`}
            text={`+${hiddenMembersCount} ${intl.formatMessage({ id: "app.crmBusiness.users" })}`}
          />
        </div>
      )}
      </div>
      {canModifyBoard && (
        <div
          className="crm-share-board-btn d-flex align-items-center whiteButton rounded p-2 pointer flex-shrink-0"
          onClick={openAssignmentUsers}
        >
          <FontAwesomeIcon
            className="mr-1"
            icon={["fas", "user-plus"]}
            size="1x"
          />
          {intl.formatMessage({ id: "app.crmBusiness.share" })}
        </div>
      )}
    </div>
  );
}
